import { graphHooks } from '@tunasong/graph-lib/react'
import { features, useThunkDispatch } from '@tunasong/redux'
import type { AccessRequestView, ApprovablePermission } from '@tunasong/schemas'
import { fillCopy, requestAccessCopy as copy, useActionToast } from '@tunasong/ui-lib'

const statusOf = (error: unknown) =>
  error && typeof error === 'object' ? (error as { status?: unknown }).status : undefined

const nameOf = (view: AccessRequestView) => view.requester.name ?? view.requester.email
const titleOf = (view: AccessRequestView) => view.entity.name ?? 'Untitled'

/**
 * Approve, decline and Undo for one request, with a terminal state for every outcome (request-access spec §8.4,
 * §8.7, §8.9). Each action resolves `true` when the row has left the list — success, 409 (someone already
 * answered) or 404 (item gone) — so the host can move focus. A failure keeps the row and offers Retry.
 *
 * Shared by the Share dialog and Home, so both behave identically.
 */
export const useAccessRequestActions = () => {
  const dispatch = useThunkDispatch()
  const { showToast, toast } = useActionToast()
  const [approveRequest, { isLoading: approving }] = graphHooks.useApproveAccessRequest()
  const [declineRequest, { isLoading: declining }] = graphHooks.useDeclineAccessRequest()
  const [reopenRequest] = graphHooks.useReopenAccessRequest()

  const alert = (severity: 'success' | 'info', message: string) =>
    dispatch(features.notifications.actions.setAlert({ severity, message }))

  /** Returns whether the row has left the list. A real failure offers Retry of the same action (§8.9 #1). */
  const settleFailure = (error: unknown, retry: () => void) => {
    const status = statusOf(error)
    if (status === 409) {
      alert('info', copy.alreadyHandled)
      return true
    }
    if (status === 404) {
      alert('info', copy.itemGone)
      return true
    }
    showToast({ message: copy.approverFailed, actionLabel: copy.retry, onAction: retry })
    return false
  }

  const approve = async (view: AccessRequestView, permission: ApprovablePermission): Promise<boolean> => {
    try {
      await approveRequest({ entityId: view.entity.id, requesterId: view.requester.userId, permission }).unwrap()
      const template = permission === 'WRITE' ? copy.approvedEdit : copy.approvedView
      alert('success', fillCopy(template, { name: nameOf(view), title: titleOf(view) }))
      return true
    } catch (error) {
      return settleFailure(error, () => void approve(view, permission))
    }
  }

  const undoDecline = async (view: AccessRequestView): Promise<void> => {
    try {
      await reopenRequest({ entityId: view.entity.id, requesterId: view.requester.userId }).unwrap()
    } catch (error) {
      settleFailure(error, () => void undoDecline(view))
    }
  }

  const decline = async (view: AccessRequestView): Promise<boolean> => {
    try {
      await declineRequest({ entityId: view.entity.id, requesterId: view.requester.userId }).unwrap()
      showToast({
        message: fillCopy(copy.declined, { name: nameOf(view) }),
        actionLabel: copy.undo,
        onAction: () => void undoDecline(view),
        duration: 6000,
      })
      return true
    } catch (error) {
      return settleFailure(error, () => void decline(view))
    }
  }

  return { approve, decline, busy: approving || declining, toast }
}

export type AccessRequestActions = ReturnType<typeof useAccessRequestActions>
